import React, { useEffect, useMemo, useState } from "react";
import api from "../../api";
import { Loader2, Download, Receipt, CreditCard, Send, X } from "lucide-react";
import { toast } from "../../hooks/use-toast";
import { downloadCsv } from "../../lib/download";
import OrderDetailModal from "../../components/OrderDetailModal";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(iso).slice(0, 16).replace("T", " "); }
};

const CHANNEL_STYLE = {
  "Online": "bg-[#eefafa] text-[#1fb0b0]",
  "Box office": "bg-amber-100 text-amber-700",
  "Comp": "bg-gray-100 text-gray-500",
};

export default function OrdersTab() {
  const [orders, setOrders] = useState(null);
  const [q, setQ] = useState(() => localStorage.getItem("gp_orders_q") || "");
  const [eventFilter, setEventFilter] = useState(() => localStorage.getItem("gp_orders_event") || "all");
  const [selected, setSelected] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState("");

  const loadOrders = () => api.get("/business/orders").then(({ data }) => setOrders(data.orders)).catch(() => setOrders([]));
  useEffect(() => { loadOrders(); }, []);
  useEffect(() => { localStorage.setItem("gp_orders_q", q); }, [q]);
  useEffect(() => { localStorage.setItem("gp_orders_event", eventFilter); }, [eventFilter]);

  const eventOpts = useMemo(() => {
    const m = {};
    (orders || []).forEach((o) => { if (o.product_id && !m[o.product_id]) m[o.product_id] = { id: o.product_id, title: o.product_title, event_date: o.event_date }; });
    return Object.values(m).sort((a, b) => (a.event_date || "").localeCompare(b.event_date || ""));
  }, [orders]);

  if (!orders) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;

  let rows = orders;
  if (eventFilter !== "all") rows = rows.filter((o) => o.product_id === eventFilter);
  if (q.trim()) { const s = q.trim().toLowerCase(); rows = rows.filter((o) => [o.buyer_name, o.buyer_email, o.order_id, o.stripe_payment_intent, o.stripe_session_id].some((f) => (f || "").toLowerCase().includes(s))); }

  const exportCsv = async () => {
    try { await downloadCsv("/business/orders.csv", "orders.csv"); }
    catch { toast({ title: "Export failed", description: "Could not download CSV", variant: "destructive" }); }
  };

  const togglePick = (oid) => setPicked((prev) => { const n = new Set(prev); n.has(oid) ? n.delete(oid) : n.add(oid); return n; });
  const allVisibleIds = rows.map((o) => o.order_id);
  const pickedVisible = allVisibleIds.filter((id) => picked.has(id));
  const allPicked = allVisibleIds.length > 0 && allVisibleIds.every((id) => picked.has(id));
  const toggleAll = () => setPicked((prev) => {
    const n = new Set(prev);
    if (allPicked) allVisibleIds.forEach((id) => n.delete(id));
    else allVisibleIds.forEach((id) => n.add(id));
    return n;
  });
  const clearPicked = () => setPicked(new Set());

  const bulkResend = async () => {
    const ids = pickedVisible;
    if (!ids.length) return;
    setBulkBusy("resend");
    try {
      const { data } = await api.post("/business/orders/bulk-resend", { order_ids: ids });
      toast({ title: "Receipts resent", description: `${data.sent} sent${data.failed ? `, ${data.failed} skipped (no valid tickets)` : ""}.` });
      clearPicked();
    } catch (e) { toast({ title: "Couldn't resend", description: e?.response?.data?.detail || "Try again.", variant: "destructive" }); }
    finally { setBulkBusy(""); }
  };

  const bulkDownload = async () => {
    const ids = pickedVisible;
    if (!ids.length) return;
    setBulkBusy("download");
    try {
      const res = await api.post("/business/orders/bulk-tickets.pdf", { order_ids: ids }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "tickets-bulk.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast({ title: "Couldn't download", description: e?.response?.data?.detail || "No valid tickets in the selected orders.", variant: "destructive" }); }
    finally { setBulkBusy(""); }
  };

  return (
    <div data-testid="orders-tab">
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} data-testid="order-event-filter"
          className="rounded-full px-4 py-2 text-sm border border-gray-200 bg-white text-[#16243b] outline-none focus:border-[#2ec9c9] max-w-full">
          <option value="all">All events</option>
          {eventOpts.map((o) => <option key={o.id} value={o.id}>{o.title}{o.event_date ? ` — ${fmtDate(o.event_date)}` : ""}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="order-search" placeholder="Search buyer, email or Stripe ref…"
          className="flex-1 min-w-[220px] rounded-full px-4 py-2 text-sm border border-gray-200 outline-none focus:border-[#2ec9c9]" />
        <button onClick={exportCsv} data-testid="orders-export-csv" className="gp-btn-outline rounded-full px-4 py-2 text-sm font-medium flex items-center gap-2"><Download size={15} /> Export CSV</button>
        <span className="text-xs text-[#8a97a8]">{rows.length} shown</span>
      </div>

      {pickedVisible.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4 rounded-xl bg-[#eefafa] border border-[#c7eeee] px-4 py-2.5" data-testid="bulk-action-bar">
          <span className="text-sm font-medium text-[#16243b]">{pickedVisible.length} order{pickedVisible.length === 1 ? "" : "s"} selected</span>
          <button onClick={bulkResend} disabled={!!bulkBusy} data-testid="bulk-resend-btn"
            className="gp-btn-teal rounded-full px-4 py-2 text-sm font-medium flex items-center gap-2 disabled:opacity-60">
            {bulkBusy === "resend" ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Resend receipts
          </button>
          <button onClick={bulkDownload} disabled={!!bulkBusy} data-testid="bulk-download-btn"
            className="gp-btn-outline rounded-full px-4 py-2 text-sm font-medium flex items-center gap-2 disabled:opacity-60">
            {bulkBusy === "download" ? <Loader2 className="animate-spin" size={15} /> : <Download size={15} />} Download tickets
          </button>
          <button onClick={clearPicked} data-testid="bulk-clear-btn" className="text-sm text-[#5a6b80] hover:text-[#16243b] flex items-center gap-1 ml-auto"><X size={14} /> Clear</button>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-[#f6f8fb] text-[#8a97a8]"><tr>
            <th className="px-4 py-3 w-10"><input type="checkbox" checked={allPicked} onChange={toggleAll} data-testid="bulk-select-all" className="w-4 h-4 accent-[#2ec9c9] align-middle" /></th>
            {["Buyer", "Event", "Purchased", "Tickets", "Channel", "Subtotal", "Booking fee", "Total", "Stripe", ""].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.order_id} data-testid={`order-row-${o.order_id}`} className="border-t border-gray-50 hover:bg-[#f9fbfd] cursor-pointer" onClick={() => setSelected(o)}>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={picked.has(o.order_id)} onChange={() => togglePick(o.order_id)} data-testid={`bulk-select-${o.order_id}`} className="w-4 h-4 accent-[#2ec9c9] align-middle" />
                </td>
                <td className="px-5 py-3"><div className="text-[#16243b] font-medium">{o.buyer_name || "—"}</div><div className="text-xs text-[#8a97a8]">{o.buyer_email}</div></td>
                <td className="px-5 py-3 text-[#5a6b80]">{o.product_title}</td>
                <td className="px-5 py-3 text-[#8a97a8] whitespace-nowrap">{fmtDate(o.purchased_at)}</td>
                <td className="px-5 py-3 text-[#16243b]">{o.ticket_count}</td>
                <td className="px-5 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${CHANNEL_STYLE[o.channel] || "bg-gray-100 text-gray-500"}`}>{o.channel}</span></td>
                <td className="px-5 py-3 text-[#5a6b80]">£{o.subtotal.toFixed(2)}</td>
                <td className="px-5 py-3 text-[#5a6b80]">£{o.booking_fee.toFixed(2)}</td>
                <td className="px-5 py-3 text-[#16243b] font-semibold">£{o.total.toFixed(2)}</td>
                <td className="px-5 py-3 text-[#8a97a8]">{o.stripe_payment_intent ? <span className="inline-flex items-center gap-1 font-mono text-xs" title={o.stripe_payment_intent}><CreditCard size={12} className="text-[#635bff]" />{o.stripe_payment_intent.slice(0, 12)}…</span> : "—"}</td>
                <td className="px-5 py-3 text-right"><Receipt size={16} className="text-[#2ec9c9] inline" /></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={11} className="px-5 py-10 text-center text-[#8a97a8]">No orders yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && <OrderDetailModal order={selected} manageable onChanged={loadOrders} onClose={() => setSelected(null)} />}
    </div>
  );
}
